import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';

const CartContext = createContext(null);
const STORAGE_KEY = 'mbk_collection_cart';
const PHONE_NUMBER = '919099302024';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Failed to parse cart from localStorage', e);
      return [];
    }
  });

  const [lastAddedItem, setLastAddedItem] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch (e) {
      console.error('Failed to save cart to localStorage', e);
    }
  }, [cart]);

  const isInCart = (id) => cart.some((item) => item.id === id);

  const addToCart = (item) => {
    if (isInCart(item.id)) {
      toast.info('Item is already in your cart');
      return;
    }
    const updated = [...cart, item];
    setCart(updated);
    setLastAddedItem(item);
    trackEvent('add_to_cart', {
      item_id: item.id,
      item_name: item.name,
      item_code: item.title,
      cart_total_items: updated.length,
    });
    toast.success('Added to your inquiry cart! 🛍️', {
      description: 'You can review and send your cart to Khushi on WhatsApp anytime.',
      duration: 3500,
    });
  };

  const removeFromCart = (id) => {
    const updated = cart.filter((item) => item.id !== id);
    setCart(updated);
    if (lastAddedItem?.id === id) {
      setLastAddedItem(null);
    }
    trackEvent('remove_from_cart', {
      item_id: id,
      cart_total_items: updated.length,
    });
    toast.info('Item removed from cart');
  };

  const toggleCart = (item) => {
    if (isInCart(item.id)) {
      removeFromCart(item.id);
    } else {
      addToCart(item);
    }
  };

  const clearCart = () => {
    const prevCount = cart.length;
    setCart([]);
    setLastAddedItem(null);
    trackEvent('clear_cart', {
      previous_items_count: prevCount,
    });
    toast.info('Cart cleared');
  };

  const getWhatsAppMessageText = (items = cart) => {
    if (!items || items.length === 0) return '';
    let text = `Hi Khushi! 👋 I'm browsing your jewellery & accessories collection on makeupbykhushi.in.\n\n`;
    text += `I loved these *${items.length} item(s)* and would like to check pricing, availability, and ordering details:\n\n`;

    items.forEach((item, index) => {
      const title = item.title || `Item #${index + 1}`;
      text += `*${index + 1}. ${title}* (${item.name || 'Photo'})\n`;
      text += `📸 Photo Preview: https://lh3.googleusercontent.com/d/${item.id}=w1000\n\n`;
    });

    const itemIds = items.map((i) => i.id).join(',');
    text += `🔗 *View My Selected Items on Website:*\nhttps://makeupbykhushi.in/my-collection?items=${itemIds}\n\n`;
    text += `Could you please share the pricing, customization options, and delivery timeline? Thank you! ✨`;
    return text;
  };

  const getWhatsAppUrl = (items = cart) => {
    const text = getWhatsAppMessageText(items);
    if (!text) return `https://wa.me/${PHONE_NUMBER}`;
    return `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        cartCount: cart.length,
        isInCart,
        addToCart,
        removeFromCart,
        toggleCart,
        clearCart,
        lastAddedItem,
        setLastAddedItem,
        getWhatsAppUrl,
        getWhatsAppMessageText,
        phoneNumber: PHONE_NUMBER,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
